import React, { useState } from 'react';
import { Shield, Car, FileText, Phone, Mail, Calendar, CreditCard, CheckCircle, ArrowRight, AlertCircle, XCircle } from 'lucide-react';
import { consultarCliente } from '../../services/consultar_cliente';

const Security = () => {
    const [formData, setFormData] = useState({
        documentType: 'DNI',
        documentNumber: '',
        birthDate: '',
        mobileNumber: '',
        phoneCompany: 'M',
        email: '',
        product: 'L1',
        brand: '',
        dealership: '',
        dataProtection: 'ACCEPTED' // Siempre será 'ACCEPTED'
    });

    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [error, setError] = useState(null);

    const phoneCompanies = [
        { id: 'M', name: 'Movistar' },
        { id: 'C', name: 'Claro' },
        { id: 'B', name: 'Bitel' },
        { id: 'E', name: 'Entel' }
    ];

    const products = [
        { id: 'L1', name: 'Autos nuevos' },
        { id: 'L2', name: 'Autos de segunda' }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProductChange = (productId) => {
        setFormData(prev => ({
            ...prev,
            product: productId
        }));
    };

    // Eliminamos handleTermsChange ya que no se necesita

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Ya no validamos dataProtection porque siempre está aceptado

        if (!formData.birthDate) {
            setError('La fecha de nacimiento es requerida');
            return;
        }

        if (!/^\d{8}$/.test(formData.documentNumber)) {
            setError('El DNI debe tener 8 dígitos numéricos');
            return;
        }

        if (!/^\d{9}$/.test(formData.mobileNumber)) {
            setError('El número de celular debe tener 9 dígitos');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            setError('Ingrese un correo electrónico válido');
            return;
        }

        setIsLoading(true);
        setError(null);
        setApiResponse(null);

        const requestBody = {
            participant: {
                identityDocument: {
                    documentType: { id: formData.documentType },
                    documentNumber: formData.documentNumber
                },
                birthDate: formData.birthDate,
                contactDetails: [
                    {
                        contact: {
                            contactType: "MOBILE",
                            mobileNumber: formData.mobileNumber,
                            phoneCompany: { id: formData.phoneCompany }
                        }
                    },
                    {
                        contact: {
                            contactType: "EMAIL",
                            address: formData.email
                        }
                    }
                ]
            },
            product: formData.product,
            vehicle: {
                brand: formData.brand || "",
                dealership: formData.dealership || ""
            },
            terms: [{
                id: "DATA_PROTECTION_LAW",
                status: { id: formData.dataProtection } // Siempre será 'ACCEPTED'
            }]
        };

        try {
            const response = await consultarCliente(requestBody);

            if (response.success === false) {
                // Caso: success: false con mensajes de error
                const errorMessage = response.data?.messages?.[0]?.message || 
                                    response.message || 
                                    'Error en la consulta';
                
                setApiResponse({
                    success: false,
                    message: errorMessage,
                    data: response.data || {},
                    messages: response.data?.messages || [],
                    hasOffers: false,
                    environment: response.environment,
                    isError: true
                });
            } else if (response.success === true) {
                // Caso: success: true
                if (response.data?.messages?.some(msg => msg.type === 'WARNING' && msg.code === '10000034')) {
                    setApiResponse({
                        success: true,
                        message: response.data.messages[0].message || "Consulta exitosa",
                        data: response.data.data || [],
                        messages: response.data.messages || [],
                        hasOffers: false,
                        environment: response.environment,
                        isError: false
                    });
                } else if (response.data?.data) {
                    setApiResponse({
                        success: true,
                        message: response.message || "Consulta exitosa",
                        data: response.data.data,
                        messages: response.data.messages || [],
                        hasOffers: true,
                        environment: response.environment,
                        isError: false
                    });
                } else {
                    setApiResponse({
                        success: true,
                        message: "Consulta completada",
                        data: [],
                        messages: [],
                        hasOffers: false,
                        environment: response.environment,
                        isError: false
                    });
                }
            } else {
                // Respuesta inesperada
                setApiResponse({
                    success: false,
                    message: 'Respuesta inesperada del servidor',
                    data: {},
                    messages: [],
                    hasOffers: false,
                    environment: 'UNKNOWN',
                    isError: true
                });
            }
        } catch (err) {
            // Solo mostrar errores de red/autenticación en el banner
            setError(err.message || 'Error al consultar la información');
            console.error('Error en la consulta:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            documentType: 'DNI',
            documentNumber: '',
            birthDate: '',
            mobileNumber: '',
            phoneCompany: 'M',
            email: '',
            product: 'L1',
            brand: '',
            dealership: '',
            dataProtection: 'ACCEPTED' // Siempre se reinicia a 'ACCEPTED'
        });
        setApiResponse(null);
        setError(null);
    };

    return (
        <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            {/* Header con logo Visioner7 */}
            <div className="flex items-center justify-between mb-8 p-4 bg-white rounded-2xl shadow-lg">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 mb-1">
                            <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                                Préstamo Vehicular Visioner7
                            </span>
                        </h1>
                        <p className="text-slate-600">Consulta tus opciones de financiamiento para vehículos</p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full flex items-center">
                        <Shield className="w-3 h-3 mr-1" />
                        Seguro
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full flex items-center">
                        API v1.0
                    </span>
                </div>
            </div>

            {/* Mostrar error general - SOLO para errores de validación/formulario */}
            {error && !apiResponse && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 className="font-medium text-red-800">Error en la consulta</h3>
                        <p className="text-red-600 text-sm mt-1">{error}</p>
                    </div>
                </div>
            )}

            {/* Tarjeta principal */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Formulario */}
                <div className="space-y-6">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                        {/* Encabezado del formulario */}
                        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold flex items-center">
                                        <FileText className="w-5 h-5 mr-2" />
                                        Datos del Solicitante
                                    </h2>
                                    <p className="text-blue-100 text-sm mt-1">Información personal requerida para la consulta</p>
                                </div>
                                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                    <CreditCard className="w-5 h-5" />
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Documento de Identidad */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                                        <FileText className="w-4 h-4 mr-2 text-blue-600" />
                                        Tipo de Documento *
                                    </label>
                                    <div className="relative">
                                        <select 
                                            name="documentType"
                                            value={formData.documentType}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all bg-white text-slate-700 appearance-none"
                                            required
                                        >
                                            <option value="DNI">DNI</option>
                                        </select>
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2 ml-6">Documento Nacional de Identidad</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Número de Documento *
                                    </label>
                                    <div className="relative">
                                        <input 
                                            type="text"
                                            name="documentNumber"
                                            value={formData.documentNumber}
                                            onChange={handleChange}
                                            placeholder="89491007"
                                            className="w-full px-4 py-3 pl-10 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                            required 
                                            pattern="[0-9]{8}"
                                            title="8 dígitos numéricos"
                                        />
                                        <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">8 dígitos exactos</p>
                                </div>
                            </div>

                            {/* Fecha de Nacimiento */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                                    <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                                    Fecha de Nacimiento *
                                </label>
                                <div className="relative">
                                    <input 
                                        type="date"
                                        name="birthDate"
                                        value={formData.birthDate}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 pl-10 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        required
                                    />
                                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                                </div>
                                <p className="text-xs text-slate-500 mt-2 ml-6">Formato: YYYY-MM-DD</p>
                            </div>

                            {/* Contacto - Móvil */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                                        <Phone className="w-4 h-4 mr-2 text-blue-600" />
                                        Número de Celular *
                                    </label>
                                    <div className="relative">
                                        <input 
                                            type="tel"
                                            name="mobileNumber"
                                            value={formData.mobileNumber}
                                            onChange={handleChange}
                                            placeholder="969505783"
                                            className="w-full px-4 py-3 pl-10 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                            required 
                                            pattern="[0-9]{9}"
                                            title="9 dígitos numéricos"
                                        />
                                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">9 dígitos exactos</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Operador *
                                    </label>
                                    <div className="relative">
                                        <select 
                                            name="phoneCompany"
                                            value={formData.phoneCompany}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all bg-white text-slate-700 appearance-none"
                                            required
                                        >
                                            <option value="">Seleccione operador</option>
                                            {phoneCompanies.map(company => (
                                                <option key={company.id} value={company.id}>{company.name}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                                    <Mail className="w-4 h-4 mr-2 text-blue-600" />
                                    Correo Electrónico *
                                </label>
                                <div className="relative">
                                    <input 
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="usuario@correo.com"
                                        className="w-full px-4 py-3 pl-10 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        required
                                    />
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                                </div>
                            </div>

                            {/* Producto */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                                    <Car className="w-4 h-4 mr-2 text-blue-600" />
                                    Tipo de Producto *
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {products.map(product => (
                                        <label 
                                            key={product.id}
                                            className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all group ${
                                                formData.product === product.id
                                                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                                                    : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
                                            }`}
                                            onClick={() => handleProductChange(product.id)}
                                        >
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                                                formData.product === product.id
                                                    ? 'border-blue-500 bg-blue-500'
                                                    : 'border-slate-300 group-hover:border-blue-300'
                                            }`}>
                                                {formData.product === product.id && (
                                                    <CheckCircle className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <div>
                                                <span className="font-medium text-slate-800">{product.name}</span>
                                                <p className="text-sm text-slate-600 mt-1">
                                                    {product.id === 'L1' ? 'Financiamiento para autos nuevos' : 'Financiamiento para autos usados'}
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Información del Vehículo */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Marca del Vehículo
                                    </label>
                                    <input 
                                        type="text"
                                        name="brand"
                                        value={formData.brand}
                                        onChange={handleChange}
                                        placeholder="Ej: Audi, Toyota, etc."
                                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Concesionaria
                                    </label>
                                    <input 
                                        type="text"
                                        name="dealership"
                                        value={formData.dealership}
                                        onChange={handleChange}
                                        placeholder="Ej: Pana"
                                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* NOTA IMPORTANTE - SE COMENTA LA SECCIÓN DE PROTECCIÓN DE DATOS */}
                            {/* 
                            Ya no se muestra al usuario porque siempre está aceptado automáticamente
                            Se envía en el requestBody como: dataProtection: 'ACCEPTED'
                            */}

                            {/* Botones */}
                            <div className="flex space-x-3 pt-4">
                                <button 
                                    type="submit"
                                    disabled={isLoading}
                                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center transform hover:-translate-y-0.5"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                            Consultando ofertas...
                                        </>
                                    ) : (
                                        <>
                                            Consultar Ofertas Visioner7
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </button>
                                <button 
                                    type="button"
                                    onClick={handleReset}
                                    className="px-6 py-3 border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-800 font-medium rounded-xl transition-all duration-200 hover:bg-slate-50"
                                >
                                    Limpiar
                                </button>
                            </div>

                            {/* Mensaje de términos automáticamente aceptados - OPCIONAL 
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 text-center">
                                <p className="text-sm text-slate-600 flex items-center justify-center">
                                    <Shield className="w-4 h-4 text-blue-500 mr-2" />
                                    Al enviar este formulario, acepta automáticamente la protección de datos personales según la Ley de Protección de Datos.
                                </p>
                            </div>*/}


                        </form>
                    </div>
                </div>

                {/* Resultados */}
                <div className="space-y-6">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                        {/* Encabezado de resultados */}
                        <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold flex items-center">
                                        <CheckCircle className="w-5 h-5 mr-2" />
                                        Resultado de la Consulta
                                    </h2>
                                    <p className="text-emerald-100 text-sm mt-1">Respuesta de la API Auto loans offer</p>
                                </div>
                                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                    <CreditCard className="w-5 h-5" />
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            {isLoading ? (
                                <div className="text-center py-12">
                                    <div className="inline-flex flex-col items-center">
                                        <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-4" />
                                        <p className="text-slate-600">Consultando información con Visioner7...</p>
                                        <p className="text-sm text-slate-500 mt-2">Esto puede tardar unos segundos</p>
                                    </div>
                                </div>
                            ) : apiResponse ? (
                                <div className="space-y-6">
                                    {/* Encabezado de respuesta - Diferentes colores según el estado */}
                                    <div className={`flex items-center justify-between p-4 rounded-xl border ${
                                        apiResponse.isError 
                                            ? 'bg-red-50 border-red-200' 
                                            : apiResponse.hasOffers 
                                                ? 'bg-emerald-50 border-emerald-200' 
                                                : 'bg-amber-50 border-amber-200'
                                    }`}>
                                        <div className="flex items-center">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                                                apiResponse.isError 
                                                    ? 'bg-red-100' 
                                                    : apiResponse.hasOffers 
                                                        ? 'bg-emerald-100' 
                                                        : 'bg-amber-100'
                                            }`}>
                                                {apiResponse.isError ? (
                                                    <XCircle className="w-4 h-4 text-red-600" />
                                                ) : apiResponse.hasOffers ? (
                                                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                                                ) : (
                                                    <AlertCircle className="w-4 h-4 text-amber-600" />
                                                )}
                                            </div>
                                            <div>
                                                <span className={`font-medium ${
                                                    apiResponse.isError 
                                                        ? 'text-red-700' 
                                                        : apiResponse.hasOffers 
                                                            ? 'text-emerald-700' 
                                                            : 'text-amber-700'
                                                }`}>
                                                    {apiResponse.message}
                                                </span>
                                                {apiResponse.messages?.length > 0 && apiResponse.messages[0].code && (
                                                    <p className="text-xs mt-1 text-slate-500">
                                                        Código: {apiResponse.messages[0].code}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <span className={`text-sm ${
                                            apiResponse.isError 
                                                ? 'text-red-600' 
                                                : apiResponse.hasOffers 
                                                    ? 'text-emerald-600' 
                                                    : 'text-amber-600'
                                        }`}>
                                            {apiResponse.environment}
                                        </span>
                                    </div>

                                    {/* Mostrar información basada en el estado */}
                                    {apiResponse.isError ? (
                                        // Mostrar mensaje de error de la API
                                        <div className="text-center py-12">
                                            <div className="inline-flex flex-col items-center">
                                                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
                                                    <XCircle className="w-8 h-8 text-red-600" />
                                                </div>
                                                <h3 className="text-slate-700 font-medium mb-2">Error en la consulta</h3>
                                                <p className="text-slate-500 text-sm max-w-xs mb-4">
                                                    {apiResponse.message}
                                                </p>
                                                <div className="text-left w-full max-w-xs">
                                                    <p className="text-xs text-slate-600 mb-2">Sugerencias:</p>
                                                    <ul className="text-xs text-slate-500 space-y-1">
                                                        <li className="flex items-start">
                                                            <span className="text-red-500 mr-1">•</span>
                                                            Verifique que el número de DNI sea correcto
                                                        </li>
                                                        <li className="flex items-start">
                                                            <span className="text-red-500 mr-1">•</span>
                                                            Asegúrese de que la fecha de nacimiento sea correcta
                                                        </li>
                                                        <li className="flex items-start">
                                                            <span className="text-red-500 mr-1">•</span>
                                                            Si el problema persiste, contacte con soporte
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ) : apiResponse.hasOffers ? (
                                        // Mostrar ofertas cuando hay disponibles
                                        <>
                                            {/* Información del cliente - SOLO si existe */}
                                            {apiResponse.data?.participant?.firstname && (
                                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                                    <h3 className="font-medium text-slate-800 mb-3 flex items-center">
                                                        <Shield className="w-4 h-4 mr-2 text-blue-600" />
                                                        Información del Cliente Visioner7
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="p-3 bg-white rounded-lg">
                                                            <span className="text-sm text-slate-500">Nombre:</span>
                                                            <p className="font-bold text-slate-800 text-lg">
                                                                {apiResponse.data.participant.firstname}
                                                            </p>
                                                        </div>
                                                        <div className="p-3 bg-white rounded-lg">
                                                            <span className="text-sm text-slate-500">Documento:</span>
                                                            <p className="font-bold text-slate-800 text-lg">
                                                                {apiResponse.data.participant.identityDocument.documentType.id} - {apiResponse.data.participant.identityDocument.documentNumber}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Ofertas - SOLO si existen */}
                                            {apiResponse.data?.offers && apiResponse.data.offers.length > 0 ? (
                                                apiResponse.data.offers.map((offer, index) => (
                                                    <div key={index} className="p-5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div>
                                                                <h4 className="font-bold text-slate-800 text-lg">Oferta #{offer.id}</h4>
                                                                <p className="text-sm text-slate-600">{offer.product?.title?.name}</p>
                                                            </div>
                                                            <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-full shadow-sm">
                                                                {offer.product?.productClassification?.id === 'L1' ? 'Auto Nuevo' : 'Auto Usado'}
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                                            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-100">
                                                                <span className="text-sm text-slate-500">Monto concedido</span>
                                                                <p className="text-2xl font-bold text-blue-700 mt-1">
                                                                    S/ {offer.grantedAmount?.amount?.toLocaleString()}
                                                                </p>
                                                            </div>
                                                            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-100">
                                                                <span className="text-sm text-slate-500">Cuota mensual</span>
                                                                <p className="text-2xl font-bold text-blue-700 mt-1">
                                                                    S/ {parseFloat(offer.installment?.amount || 0).toFixed(2)}
                                                                </p>
                                                            </div>
                                                            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-100">
                                                                <span className="text-sm text-slate-500">Tasa de interés</span>
                                                                <p className="text-2xl font-bold text-emerald-600 mt-1">
                                                                    {parseFloat(offer.interestRate?.percent || 0).toFixed(2)}%
                                                                </p>
                                                            </div>
                                                            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-100">
                                                                <span className="text-sm text-slate-500">Plazo</span>
                                                                <p className="text-2xl font-bold text-slate-800 mt-1">
                                                                    {offer.term?.termNumber} meses
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-8">
                                                    <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                                                    <p className="text-slate-600">No se encontraron ofertas disponibles</p>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Mostrar mensaje cuando no hay ofertas (pero no es un error)
                                        <div className="text-center py-12">
                                            <div className="inline-flex flex-col items-center">
                                                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                                                    <AlertCircle className="w-8 h-8 text-amber-600" />
                                                </div>
                                                <h3 className="text-slate-700 font-medium mb-2">Sin ofertas disponibles</h3>
                                                <p className="text-slate-500 text-sm max-w-xs mb-4">
                                                    {apiResponse.message || "No cuenta con ofertas comerciales en este momento"}
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                    Nuestros ejecutivos se pondrán en contacto para evaluar otras opciones.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Estado inicial (sin consultas realizadas)
                                <div className="text-center py-12">
                                    <div className="inline-flex flex-col items-center">
                                        <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mb-4">
                                            <CreditCard className="w-8 h-8 text-slate-400" />
                                        </div>
                                        <h3 className="text-slate-700 font-medium mb-2">Consulta tu préstamo vehicular</h3>
                                        <p className="text-slate-500 text-sm max-w-xs">
                                            Completa el formulario para conocer las ofertas de financiamiento disponibles
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Security;